import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Page, Pageable } from '../../../model/rest/base';
import { ReportResponse } from '../../../model/report';
import {
  invoiceReportsFromGeneric,
  Subscription,
  SubscriptionConfig,
  SubscriptionEvent,
  SubscriptionInvoiceReport,
  SubscriptionProductType,
  SubscriptionStatus,
  SubscriptionUpdateRequest,
} from '../../../model/v3/subscriptions';

import { appendIfDefined, createPageableParams } from './rest-v3-utils';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionsV3RestService {
  constructor(private http: HttpClient) {}

  private baseUrl(path: string = ''): string {
    return `${environment.eventsApiBase}/v1/subscriptions${path}`;
  }

  getInvoiceReports(
    month: string,
    schoolId?: number | null,
  ): Observable<SubscriptionInvoiceReport[]> {
    let params = new HttpParams().set('month', month);
    if (schoolId != null) {
      params = params.set('schoolIdParam', schoolId);
    }
    return this.http
      .get<ReportResponse>(
        `${environment.eventsApiBase}/v1/reports/subscriptions-invoice`,
        { params },
      )
      .pipe(map(invoiceReportsFromGeneric));
  }

  getAuditReport(
    month: string,
    schoolId?: number | null,
    pageable?: Pageable,
  ): Observable<Page<SubscriptionEvent>> {
    let params = createPageableParams(pageable);
    params = params.set('month', month);
    if (schoolId != null) {
      params = params.set('schoolId', schoolId);
    }
    return this.http.get<Page<SubscriptionEvent>>(
      this.baseUrl('/reports/audit'),
      { params },
    );
  }

  getConfig(schoolId: number): Observable<SubscriptionConfig> {
    return this.http.get<SubscriptionConfig>(
      this.baseUrl(`/schools/${schoolId}/config`),
    );
  }

  updateConfig(
    schoolId: number,
    config: SubscriptionConfig,
  ): Observable<SubscriptionConfig> {
    return this.http.put<SubscriptionConfig>(
      this.baseUrl(`/schools/${schoolId}/config`),
      config,
    );
  }

  getSchoolInvoiceReport(
    schoolId: number,
    month: string,
  ): Observable<SubscriptionInvoiceReport[]> {
    return this.http
      .get<ReportResponse>(
        `${environment.eventsApiBase}/v1/school/${schoolId}/reports/subscriptions-invoice`,
        { params: new HttpParams().set('month', month) },
      )
      .pipe(map(invoiceReportsFromGeneric));
  }

  getSchoolAuditReport(
    schoolId: number,
    month: string,
    pageable?: Pageable,
  ): Observable<Page<SubscriptionEvent>> {
    let params = createPageableParams(pageable);
    params = params.set('month', month);
    return this.http.get<Page<SubscriptionEvent>>(
      this.baseUrl(`/schools/${schoolId}/reports/audit`),
      { params },
    );
  }

  listSubscriptions(
    schoolId: number,
    options: {
      productType?: SubscriptionProductType | null;
      status?: SubscriptionStatus | null;
      personId?: number | null;
      pageable?: Pageable;
    } = {},
  ): Observable<Page<Subscription>> {
    let params = createPageableParams(options.pageable);
    params = appendIfDefined(params, 'productType', options.productType);
    params = appendIfDefined(params, 'status', options.status);
    params = appendIfDefined(params, 'personId', options.personId);
    return this.http.get<Page<Subscription>>(
      this.baseUrl(`/schools/${schoolId}`),
      { params },
    );
  }

  getSubscriptionEvents(
    schoolId: number,
    subscriptionId: number,
  ): Observable<SubscriptionEvent[]> {
    return this.http.get<SubscriptionEvent[]>(
      this.baseUrl(`/schools/${schoolId}/${subscriptionId}/events`),
    );
  }

  updateSubscription(
    schoolId: number,
    subscriptionId: number,
    request: SubscriptionUpdateRequest,
  ): Observable<Subscription> {
    return this.http.patch<Subscription>(
      this.baseUrl(`/schools/${schoolId}/${subscriptionId}`),
      request,
    );
  }

}
